<template>
  <sec-section-heading :id="headingId" :eyebrow="eyebrow" :title="title" />
  <div ref="root" class="sec-faq">
    <div
      v-for="(item, i) in items"
      :key="item.q"
      class="sec-faq__item"
      :class="{ 'sec-faq__item--open': open === i }"
      data-reveal
    >
      <h3 class="sec-faq__heading">
        <button
          :id="`${uid}-q${i}`"
          type="button"
          class="o600 sec-faq__q"
          :aria-expanded="open === i ? 'true' : 'false'"
          :aria-controls="`${uid}-a${i}`"
          @click="toggle(i)"
        >
          <span>{{ item.q }}</span>
          <span class="sec-faq__icon" aria-hidden="true"></span>
        </button>
      </h3>
      <div
        :id="`${uid}-a${i}`"
        :ref="(el) => (panels[i] = el)"
        class="sec-faq__panel"
        role="region"
        :aria-labelledby="`${uid}-q${i}`"
        :inert="open !== i"
      >
        <p class="m500 sec-faq__a">{{ item.a }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
// Centred FAQ accordion. Wrap in <sec-section :labelledby="headingId">.
// One answer open at a time (the first by default); answers open and close with a GSAP height
// tween (instant under reduced motion). Closed answers stay in the DOM (inert) and the same
// items feed the FAQPage JSON-LD through <sec-page :faq>.
import { ref, onMounted, getCurrentInstance } from 'vue';
import { gsap, prefersReducedMotion } from '@/plugins/gsap';
import SecSectionHeading from './SecSectionHeading.vue';

const props = defineProps({
  eyebrow: { type: String, default: 'Questions' },
  title: { type: String, required: true },
  items: { type: Array, required: true }, // [{ q, a }]
  headingId: { type: String, default: undefined },
});

const uid = props.headingId || `sec-faq-${getCurrentInstance().uid}`;
const open = ref(0);
const panels = [];
const time = (d) => (prefersReducedMotion() ? 0 : d);

onMounted(() => {
  panels.forEach((el, i) => {
    if (el && i !== open.value) gsap.set(el, { height: 0 });
  });
});

function toggle(i) {
  const prev = open.value;
  open.value = prev === i ? null : i;
  if (prev !== null && panels[prev]) {
    gsap.to(panels[prev], { height: 0, duration: time(0.35), ease: 'power2.inOut', overwrite: true });
  }
  if (open.value !== null && panels[i]) {
    gsap.fromTo(
      panels[i],
      { height: 0 },
      { height: 'auto', duration: time(0.5), ease: 'expo.out', overwrite: true, clearProps: 'height' }
    );
  }
}
</script>

<style scoped>
.sec-faq {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 880px;
  margin-inline: auto;
  text-align: left;
}

.sec-faq__item {
  background: var(--sec-surface);
  border: 1px solid var(--sec-line);
  border-radius: 14px;
  transition: border-color 0.25s ease, box-shadow 0.25s ease;
}

.sec-faq__item:hover {
  border-color: var(--sec-line-strong);
}

.sec-faq__item--open {
  border-color: var(--sec-accent);
  box-shadow: 0 18px 36px -24px var(--sec-header-shadow);
}

.sec-faq__heading {
  margin: 0;
}

.sec-faq__q {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sec-s2);
  width: 100%;
  padding: 1.125rem 1.25rem;
  font-size: 1.0625rem;
  line-height: 1.4;
  text-align: left;
  color: var(--sec-ink);
  background: none;
  border: 0;
  border-radius: 14px;
  cursor: pointer;
}

.sec-faq__q:focus-visible {
  outline: 2px solid var(--sec-accent);
  outline-offset: 2px;
}

/* Plus that turns into a minus. */
.sec-faq__icon {
  position: relative;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--sec-accent-subtle);
  transition: background-color 0.25s ease, transform 0.35s ease;
}

.sec-faq__icon::before,
.sec-faq__icon::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 12px;
  height: 2px;
  border-radius: 2px;
  background: var(--sec-accent-text);
  transform: translate(-50%, -50%);
  transition: transform 0.35s ease, background-color 0.25s ease;
}

.sec-faq__icon::after {
  transform: translate(-50%, -50%) rotate(90deg);
}

.sec-faq__item--open .sec-faq__icon {
  background: var(--sec-accent-strong);
  transform: rotate(180deg);
}

.sec-faq__item--open .sec-faq__icon::before,
.sec-faq__item--open .sec-faq__icon::after {
  background: var(--sec-on-accent);
}

.sec-faq__item--open .sec-faq__icon::after {
  transform: translate(-50%, -50%) rotate(0deg);
}

.sec-faq__panel {
  overflow: hidden;
}

.sec-faq__a {
  padding: 0 1.25rem 1.25rem;
  font-size: 1rem;
  line-height: 1.65;
  color: var(--sec-ink-2);
}

@media only screen and (min-width: 700px) {
  .sec-faq__q {
    padding: 1.375rem 1.75rem;
    font-size: 1.125rem;
  }
  .sec-faq__a {
    padding: 0 1.75rem 1.5rem;
    font-size: 1.0625rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .sec-faq__item,
  .sec-faq__icon,
  .sec-faq__icon::before,
  .sec-faq__icon::after {
    transition: none;
  }
}
</style>
